// Image de partage Open Graph (1200 × 630) : /?sheet=og, capturée par scripts/og-image.ts dans client/public/og-image.png.
// Tanks, formes et projectiles dessinés par le code du jeu ; textes en polices système (titres Bahnschrift/DIN).
import { useEffect, useRef } from 'react';
import { Application, Container, Graphics } from 'pixi.js';
import { getClass } from '../../../shared/catalog/classes';
import { STYLE_CODE } from '../../../shared/catalog/variants';
import { SHAPES } from '../../../shared/shapes';
import { ACCENT_ENEMY, ACCENT_SELF, ART_R, drawShape } from '../game/render/art';
import { drawTankParts } from '../game/render/variants';

const W = 1200;
const H = 630;

/** Tank vu de dessus, avec son ombre portée : `hull` = sens de la marche, `aim` = visée de la tourelle (radians). */
function tank(classId: string, code: number, accent: number, x: number, y: number, radius: number, hull: number, aim: number): Container {
  const parts = { hull: new Graphics(), turret: new Graphics(), auto: new Graphics() };
  drawTankParts(parts, getClass(classId), code, accent);
  parts.hull.rotation = hull;
  parts.turret.rotation = aim;
  parts.auto.rotation = aim + Math.PI / 3;
  const shadow = new Graphics(parts.hull.context);
  shadow.rotation = hull;
  shadow.tint = 0x000000;
  shadow.alpha = 0.35;
  shadow.position.set(2.5, 4);
  const root = new Container();
  root.addChild(shadow, parts.hull, parts.turret, parts.auto);
  root.position.set(x, y);
  root.scale.set(radius / ART_R);
  return root;
}

function shape(kind: number, x: number, y: number, scale: number, rotation: number): Graphics {
  const g = new Graphics();
  drawShape(g, SHAPES[kind]);
  g.position.set(x, y);
  g.scale.set(scale);
  g.rotation = rotation;
  return g;
}

/** Projectile du jeu (cercle cerclé d'encre), alignés le long de la visée. */
function shots(x: number, y: number, aim: number, from: number, gap: number, n: number, r: number, color: number, ink: number): Graphics {
  const g = new Graphics();
  for (let i = 0; i < n; i++) {
    const d = from + i * gap;
    g.circle(x + Math.cos(aim) * d, y + Math.sin(aim) * d, r).fill(color).stroke({ width: r * 0.25, color: ink });
  }
  return g;
}

function scene(): Container {
  const s = new Container();
  const hero = { x: 880, y: 352 };
  const foe = { x: 1098, y: 128 };
  const foe2 = { x: 1118, y: 540 };
  const toFoe = Math.atan2(foe.y - hero.y, foe.x - hero.x);
  const toHero = (p: { x: number; y: number }) => Math.atan2(hero.y - p.y, hero.x - p.x);
  s.addChild(
    shape(2, 700, 520, 1.25, 0.3),
    shape(1, 770, 118, 1.2, 0.9),
    shape(0, 664, 250, 1.2, 0.25),
    shape(0, 1010, 610, 1.1, 0.6),
    shape(3, 1168, 352, 1.1, 0.2),
    shape(1, 652, 404, 1.0, 2.1),
    // Les tirs adverses manquent leur cible (décalés de la ligne de visée) pour ne pas se mêler à ceux du joueur.
    shots(foe.x, foe.y, toHero(foe) + 0.24, 66, 56, 2, 11, ACCENT_ENEMY, 0x4a1a10),
    shots(foe2.x, foe2.y, toHero(foe2) - 0.2, 60, 0, 1, 10, ACCENT_ENEMY, 0x4a1a10),
    tank('C06-F', STYLE_CODE.obsidienne, ACCENT_ENEMY, foe.x, foe.y, 40, 2.5, toHero(foe) + 0.24),
    tank('C02-A', STYLE_CODE.desert, ACCENT_ENEMY, foe2.x, foe2.y, 34, -2.3, toHero(foe2) - 0.2),
    shots(hero.x, hero.y, toFoe, 206, 54, 2, 13, ACCENT_SELF, 0x1a3b48),
    tank('C01-A', STYLE_CODE.boreal, ACCENT_SELF, hero.x, hero.y, 96, -0.7, toFoe),
  );
  return s;
}

const plate = 'repeating-linear-gradient(-45deg, #f2a23a 0 14px, #1b1c1f 14px 28px)';

export function OgSheet() {
  const host = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = host.current;
    if (!el) return;
    const app = new Application();
    let destroyed = false;
    void app.init({ width: W, height: H, backgroundAlpha: 0, antialias: true, resolution: 1 }).then(() => {
      if (destroyed) return;
      el.appendChild(app.canvas);
      app.stage.addChild(scene());
      app.render();
      // Signal pour la capture : la scène est dessinée.
      document.body.dataset.ogReady = '1';
    });
    return () => {
      destroyed = true;
      app.destroy({ removeView: true }, { children: true });
    };
  }, []);

  return (
    <div
      style={{
        position: 'relative',
        width: W,
        height: H,
        overflow: 'hidden',
        background: [
          'radial-gradient(ellipse 70% 90% at 78% 50%, rgba(57, 208, 255, 0.07), transparent 70%)',
          'linear-gradient(90deg, rgba(14, 16, 19, 0.96) 0%, rgba(14, 16, 19, 0.82) 44%, rgba(14, 16, 19, 0) 62%)',
          'linear-gradient(#1d232a 1px, transparent 1px) 0 0 / 44px 44px',
          'linear-gradient(90deg, #1d232a 1px, transparent 1px) 0 0 / 44px 44px',
          '#151a20',
        ].join(', '),
        color: 'var(--text)',
      }}
    >
      <div ref={host} style={{ position: 'absolute', inset: 0 }} aria-hidden />
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(14, 16, 19, 0.9) 0%, rgba(14, 16, 19, 0.55) 40%, transparent 56%)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', left: 76, top: 112, width: 600, display: 'flex', flexDirection: 'column', gap: 22 }}>
        <div
          className="display"
          style={{ fontSize: 128, lineHeight: 0.92, letterSpacing: '0.05em', fontStretch: 'condensed', textShadow: '0 4px 0 rgba(0, 0, 0, 0.45)' }}
        >
          TANK<span style={{ color: 'var(--accent)' }}>SHIFT</span>
        </div>
        <div style={{ width: 300, height: 12, background: plate, borderRadius: 2 }} />
        <div className="display" style={{ fontSize: 38, letterSpacing: '0.08em', color: '#f4efe4' }}>
          Évolue. Domine. Recommence.
        </div>
        <div style={{ fontSize: 25, lineHeight: 1.35, color: '#b9b9b3', maxWidth: 520 }}>Jeu de tanks multijoueur gratuit, dans le navigateur, sur PC et sur téléphone.</div>
      </div>
      <div
        className="display"
        style={{
          position: 'absolute',
          left: 76,
          bottom: 62,
          padding: '9px 18px 8px',
          border: '2px solid var(--accent)',
          borderRadius: 4,
          background: 'rgba(242, 162, 58, 0.1)',
          color: 'var(--accent-hi)',
          fontSize: 27,
          letterSpacing: '0.1em',
          textTransform: 'none',
        }}
      >
        tankshift.com
      </div>
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 14, background: plate }} />
    </div>
  );
}
